import { Col, Empty, Flex, Row } from 'antd';
import { Splitter } from 'antd';
import { DateTime } from 'luxon';
import React, { ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AlertEmptyProject from '../components/alertEmptyProject';
import {
    BORDER_MAIN,
    COLOR_EMPTY_SPACE,
    FORMAT_DATE,
    HEIGHT_HEADER_ROW,
    HEIGHT_ITEM,
    HEIGHT_SCROLL,
    WIDTH_PERCENT_LEFT_HEADER,
    WIDTH_PERCENT_LEFT_HEADER_MAX,
} from '../const';
import { getHeaderRowsByHeaderType, getStartOfWeek } from '../functions';
import useHeightGantContainer from '../hooks/useHeightGantContainer';
import useOs from '../hooks/useOs';
import { useSettings } from '../hooks/useSettings';
import { useSyncCalendarDate } from '../hooks/useSyncCalendarDate';
import useWidthContainer from '../hooks/useWidthContainer';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setterGantScroll } from '../store/slices/gantScrollSlice';
import { setterGant } from '../store/slices/gantSlice';
import { HeaderType, ModeTimeLineType } from '../types';
import GantSettingPanel from './gantSettingPanel/gantSettingPanel';
import { useScrollToCurrentItem } from './hook/useScrollToCurrentItem';
import { useScrollContext } from './scrollContext';
import useScrollbarSize from './useScrollbarSize';
import { useWidthLeftHeaderContext } from './widthLeftColumnContext';

export interface IGantChartLayout {
    isInputItemsExists?: boolean;
    contentAreaLeftTop: ReactNode;
    contentAreaRightTop: ReactNode;
    contentAreaLeftBottom: ReactNode;
    contentAreaRightBottom: ReactNode;
    gridGantRef: React.RefObject<HTMLDivElement>;
    headerLeftGantRef: React.RefObject<HTMLDivElement>;
    paddingBottom?: number;
}

const GantChartLayout: React.FC<IGantChartLayout> = ({
    contentAreaLeftTop,
    contentAreaRightTop,
    contentAreaRightBottom,
    isInputItemsExists = false,
    gridGantRef,
    headerLeftGantRef,
    contentAreaLeftBottom,
    paddingBottom = 0,
}) => {
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const { setWidthLeftHeader, widthLeftHeader } = useWidthLeftHeaderContext();
    const dispatch = useDispatch<ComponentsDispatch>();
    const os = useOs();

    const containerHeightRef = useRef<HTMLDivElement>(null);
    const containerParentRef = useRef<HTMLDivElement>(null);

    const isVisible = true;

    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const modeTimeLine = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);
    const countVisibleRow = useSelector<RootState, number>((state) => state.gant.countVisibleRow);
    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const endDateExtend = useSelector<RootState, string>((state) => state.gant.endDateExtend);
    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);
    const currentScrollX = useSelector<RootState, number>((state) => state.gantScroll.currentScrollX);
    const currentScrollY = useSelector<RootState, number>((state) => state.gantScroll.currentScrollY);

    const { saveUserSettings } = useSettings();
    const { height } = useScrollbarSize();
    const { setScrollX } = useScrollContext();

    const headerGantRef = useRef<HTMLDivElement>(null);

    const availableHeight = useHeightGantContainer(containerHeightRef) - paddingBottom;
    useWidthContainer(containerParentRef);

    useEffect(() => {
        if (!isVisible) {
            dispatch(setterGantItems({ currentGantItem: null, isItemEdit: false }));
        } else {
            if (headerLeftGantRef.current) {
                headerLeftGantRef.current.scrollTop = currentScrollY;
            }
            if (gridGantRef.current) {
                gridGantRef.current.scrollTop = currentScrollY;
            }
        }
    }, [isVisible]);

    useEffect(() => {
        if (gridGantRef.current) {
            gridGantRef.current.scrollLeft = currentScrollX || 0;
        }

        if (headerGantRef.current) {
            headerGantRef.current.scrollLeft = currentScrollX || 0;
        }
    }, [isVisible, currentScrollX]);

    useEffect(() => {
        if (gridGantRef.current && gridGantRef.current.scrollTop !== currentScrollY) {
            gridGantRef.current.scrollTop = currentScrollY;
        }
    }, [currentScrollY]);

    const start = DateTime.fromISO(startDateExtend);
    const end = DateTime.fromISO(endDateExtend);
    const fullLengthGantInDays = Math.ceil(end.diff(start, 'days').days) + 1;

    const headerRows = getHeaderRowsByHeaderType(headerType, modeTimeLine);

    const heightHeader = headerRows.length * HEIGHT_HEADER_ROW;

    const itemsHeight = countVisibleRow * HEIGHT_ITEM;
    const availableHeightWithScroll = availableHeight - HEIGHT_SCROLL;
    const svgHeight = Math.max(itemsHeight, availableHeightWithScroll) - 1;

    const isShowVerticalScroll = itemsHeight > availableHeightWithScroll;
    const isWindows = os === 'Windows';

    useEffect(() => {
        if (!startDateExtend || !widthColumn) {
            return;
        }

        const days = Math.round((currentScrollX || 0) / widthColumn);

        const newCurrentOffsetDate = DateTime.fromISO(startDateExtend).plus({
            days,
        });

        if (modeTimeLine === 'pagination') {
            dispatch(
                setterGant({
                    currentOffsetDate:
                        headerType === 'week'
                            ? getStartOfWeek(newCurrentOffsetDate).toFormat(FORMAT_DATE)
                            : newCurrentOffsetDate.startOf(headerType).toFormat(FORMAT_DATE),
                }),
            );
        } else {
            dispatch(
                setterGant({
                    currentOffsetDate: newCurrentOffsetDate.toFormat(FORMAT_DATE),
                }),
            );
        }
    }, [currentScrollX, widthColumn, startDateExtend, endDateExtend]);

    const handleScrollX = useCallback((scrollLeft: number) => {
        if (headerGantRef.current && gridGantRef.current) {
            headerGantRef.current.scrollLeft = scrollLeft;
            gridGantRef.current.scrollLeft = scrollLeft;

            setScrollX(scrollLeft);
        }
    }, []);

    useScrollToCurrentItem(handleScrollX);
    useSyncCalendarDate();

    const handleScrollY = (scrollTop: number) => {
        if (headerLeftGantRef.current && gridGantRef.current) {
            headerLeftGantRef.current.scrollTop = scrollTop;
            gridGantRef.current.scrollTop = scrollTop;

            dispatch(setterGantScroll({ currentScrollY: scrollTop }));
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollLeft, scrollTop } = e.currentTarget;

        handleScrollX(scrollLeft);
        handleScrollY(scrollTop);
    };

    const handleLeftVerticalScroll = (e: React.WheelEvent<HTMLDivElement>) => {
        if (gridGantRef.current) {
            gridGantRef.current.scrollTop += e.deltaY;
        }
    };

    return (
        <div
            ref={containerParentRef}
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minWidth: 0,
                height: `calc(100% - ${paddingBottom ?? 0}px)`,
            }}
        >
            <GantSettingPanel gantBodyRef={gridGantRef} treeRef={headerLeftGantRef} gantHeaderRef={headerGantRef} />

            <Splitter
                style={{ minHeight: 0 }}
                onResizeEnd={([widthColumn]) => {
                    saveUserSettings({
                        projectsTreePanelWidth: widthColumn,
                    });

                    dispatch(
                        setterGant({
                            widthLeftHeader: widthColumn,
                        }),
                    );
                }}
                onResize={([widthColumn]) => {
                    setWidthLeftHeader(widthColumn);
                }}
            >
                <Splitter.Panel
                    size={widthLeftHeader}
                    min={`${WIDTH_PERCENT_LEFT_HEADER}%`}
                    max={`${WIDTH_PERCENT_LEFT_HEADER_MAX}%`}
                    style={{
                        borderRight: BORDER_MAIN,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Row style={{ flex: '0 0 auto' }}>
                        <Col span={24} style={{ borderBottom: BORDER_MAIN }}>
                            <Flex
                                align="center"
                                style={{
                                    backgroundColor: COLOR_EMPTY_SPACE,
                                    height: heightHeader,
                                    padding: '0 5px',
                                }}
                            >
                                {contentAreaLeftTop}
                            </Flex>
                        </Col>
                    </Row>

                    {isVisible && (
                        <div style={{ width: '100%' }} onWheel={handleLeftVerticalScroll}>
                            {isInputItemsExists ? (
                                <div
                                    ref={headerLeftGantRef}
                                    style={{
                                        overflow: 'hidden',
                                        height: availableHeight - paddingBottom - height,
                                        userSelect: 'none',
                                    }}
                                >
                                    {contentAreaLeftBottom}
                                </div>
                            ) : (
                                <Empty
                                    style={{
                                        height: availableHeight - paddingBottom,
                                        padding: '50px 10px',
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Splitter.Panel>

                <Splitter.Panel
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Row style={{ flex: '0 0 auto' }}>
                        <Col
                            span={24}
                            style={{
                                borderBottom: BORDER_MAIN,
                                backgroundColor: COLOR_EMPTY_SPACE,
                            }}
                        >
                            <div
                                ref={headerGantRef}
                                style={{
                                    overflow: 'hidden',
                                    backgroundColor: 'white',
                                    height: heightHeader,
                                    marginRight: isShowVerticalScroll && isWindows ? 17 : 0,
                                    borderRight: isShowVerticalScroll && isWindows ? 'none' : BORDER_MAIN,
                                    userSelect: 'none',
                                }}
                            >
                                {!!fullLengthGantInDays && (
                                    <svg width={fullLengthGantInDays * widthColumn} height={heightHeader}>
                                        {contentAreaRightTop}
                                    </svg>
                                )}
                            </div>
                        </Col>
                    </Row>
                    {isVisible && (
                        <Row ref={containerHeightRef} style={{ flex: '1 1 auto', minHeight: 0 }}>
                            <Col span={24} style={{ height: `calc(100% - ${paddingBottom}px)` }}>
                                {isInputItemsExists ? (
                                    <div
                                        ref={gridGantRef}
                                        style={{
                                            overflow: 'auto',
                                            height: '100%',
                                        }}
                                        onScroll={handleScroll}
                                    >
                                        {!!fullLengthGantInDays && (
                                            <svg width={fullLengthGantInDays * widthColumn} height={svgHeight}>
                                                {contentAreaRightBottom}
                                            </svg>
                                        )}
                                    </div>
                                ) : filterProject.length ? (
                                    <Empty
                                        style={{
                                            height: availableHeight - paddingBottom,
                                            padding: '50px 10px',
                                        }}
                                    />
                                ) : (
                                    <AlertEmptyProject />
                                )}
                            </Col>
                        </Row>
                    )}
                </Splitter.Panel>
            </Splitter>
        </div>
    );
};

export default GantChartLayout;
